import { Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { Activity, Box, Eye, FileBox, FileText, Network, Unlink } from "lucide-react";
import { type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router";

import {
  entityIdFromPath,
  type EntityLink,
  type EntityLinkType,
  type EntityLinkVerdict,
} from "~/ai-chat/model/entity-link";
import { type FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import { type MeshViewRequest, viewableMeshFormat } from "~/ai-chat/model/mesh-file-types";
import { ChatCopyButton } from "~/ai-chat/ui/chat-copy-button";
import { cn } from "~/utils/cn";

export interface ChatEntityChipProps {
  link: EntityLink;
  navigationPath: string;
  children: ReactNode;
  // Open the mesh in the inline viewer (rendered by the enclosing message). Absent
  // when the chip isn't inside a message that can host a viewer.
  onViewMesh?: (mesh: MeshViewRequest) => void;
  // Open a non-mesh file in the inline preview (rendered by the enclosing
  // message). Absent when the chip isn't inside a message that can host one.
  onPreview?: (file: FilePreviewRequest) => void;
  // Whether the target is confirmed to exist. Only a found chip navigates; the default
  // suits chips built from a real object (e.g. a just-saved resource).
  verdict?: EntityLinkVerdict;
}

const ENTITY_ICONS: Record<EntityLinkType, typeof Network> = {
  system: Network,
  model: FileText,
  artifact: FileBox,
  job: Activity,
};

const PILL_CLASSES =
  "bg-background/60 inline-flex max-w-full items-baseline gap-1 rounded-full pr-1.5 align-baseline font-sans text-xs font-medium shadow-[inset_0_0_0_1px_var(--border)] transition-[background-color] duration-150";
const LABEL_CLASSES = "inline-flex min-w-0 items-baseline gap-1 py-0.5 pl-2";

export function ChatEntityChip({
  link,
  navigationPath,
  children,
  onViewMesh,
  onPreview,
  verdict = "found",
}: ChatEntityChipProps) {
  const Icon = ENTITY_ICONS[link.type];
  const entityId = entityIdFromPath(link.path);

  if (verdict !== "found") {
    return (
      <InertEntityChip link={link} verdict={verdict}>
        {children}
      </InertEntityChip>
    );
  }

  // A file chip (model/artifact) carries the file name as its text. Meshes get a
  // View 3D button; any other file gets a Preview button (the two are exclusive).
  const fileName = typeof children === "string" ? children : "";
  const isFileChip = link.type === "model" || link.type === "artifact";
  const meshFormat = fileName ? viewableMeshFormat(fileName) : undefined;
  const canView3d = Boolean(meshFormat && entityId && onViewMesh && isFileChip);
  const canPreview = Boolean(!meshFormat && entityId && onPreview && isFileChip && fileName);
  // The copy button sits beside the Link (never inside it — nested interactive elements are
  // invalid and steal the anchor's clicks), so the pill wrapper carries the shared styling.
  return (
    <span className={cn(PILL_CLASSES, "text-foreground hover:bg-background")}>
      <Link
        to={navigationPath}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => focusMainWindowIfExternal(event.currentTarget)}
        className={cn(LABEL_CLASSES, "transition-[scale] duration-150 active:scale-[0.96]")}
      >
        <Icon className="text-muted-foreground size-3 shrink-0 self-center" aria-hidden="true" />
        <span className="min-w-0 truncate">{children}</span>
      </Link>
      {canView3d && meshFormat && entityId ? (
        <button
          type="button"
          onClick={() => onViewMesh?.({ resourceId: entityId, name: fileName, format: meshFormat })}
          aria-label={`View ${fileName} in 3D`}
          className="text-muted-foreground hover:text-foreground shrink-0 self-center rounded p-1 transition-colors"
        >
          <Box className="size-3" aria-hidden="true" />
        </button>
      ) : null}
      {canPreview && entityId ? (
        <button
          type="button"
          onClick={() => onPreview?.({ resourceId: entityId, name: fileName })}
          aria-label={`Preview ${fileName}`}
          className="text-muted-foreground hover:text-foreground shrink-0 self-center rounded p-1 transition-colors"
        >
          <Eye className="size-3" aria-hidden="true" />
        </button>
      ) : null}
      {entityId ? <CopyEntityIdButton entityId={entityId} type={link.type} /> : null}
    </span>
  );
}

// A popped-out chat renders through a portal into an external window (Document PiP or
// window.open), while navigation still happens on the main window's router — without this,
// clicking a chip in the popout changes a window the user may not be looking at and reads
// as a dead link. No-op in the docked chat, where the chip's document is the main window's.
export function focusMainWindowIfExternal(node: Element): void {
  const clickedIn = node.ownerDocument.defaultView;
  if (clickedIn && clickedIn !== window) {
    window.focus();
  }
}

interface InertEntityChipProps {
  link: EntityLink;
  verdict: Exclude<EntityLinkVerdict, "found">;
  children: ReactNode;
}

// The LLM can hallucinate an id, so a chip stays inert until its target is confirmed. The
// id remains copyable so the user can still look it up or report it.
function InertEntityChip({ link, verdict, children }: InertEntityChipProps) {
  const entityId = entityIdFromPath(link.path);
  const hint = inertHint(verdict, link.type);
  const Icon = hint ? Unlink : ENTITY_ICONS[link.type];
  const label = (
    <span
      tabIndex={hint ? 0 : undefined}
      aria-busy={hint ? undefined : true}
      className={cn(LABEL_CLASSES, hint ? "decoration-muted-foreground/60 line-through" : "opacity-70")}
    >
      <Icon className="size-3 shrink-0 self-center" aria-hidden="true" />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );

  return (
    <span className={cn(PILL_CLASSES, "text-muted-foreground")}>
      {hint ? (
        <Tooltip>
          <TooltipTrigger asChild>{label}</TooltipTrigger>
          <TooltipContent side="top">{hint}</TooltipContent>
        </Tooltip>
      ) : (
        label
      )}
      {entityId ? <CopyEntityIdButton entityId={entityId} type={link.type} /> : null}
    </span>
  );
}

function inertHint(verdict: Exclude<EntityLinkVerdict, "found">, type: EntityLinkType): string | null {
  const label = type === "model" ? "resource" : type;
  switch (verdict) {
    case "pending":
      return null;
    case "missing":
      return `This ${label} couldn't be found. It may not exist, or you may not have access to it.`;
    case "unverified":
      return `Couldn't check whether this ${label} exists.`;
  }
}

function CopyEntityIdButton({ entityId, type }: { entityId: string; type: EntityLinkType }) {
  return (
    <ChatCopyButton
      text={entityId}
      copiedMessage="ID copied."
      ariaLabel={`Copy ${type} ID`}
      className="self-center rounded p-1"
    />
  );
}
